import { PRESET_THEMES } from "@/src/constants/appearance";
import { useAuth } from "@/src/contexts/auth.context";
import { useTheme } from "@/src/styles/theme-provider";
import { derivePalette } from "@/src/utils/derive-palette";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import ColorModeSection, { ColorMode } from "./color-mode-section";
import CustomThemesSection from "./custom-themes-section";
import OrgLogoSection from "./org-logo-section";
import PresetThemesSection from "./preset-themes-section";
import { useGetOrganizationColorQuery, useUpdateOrganizationColorMutation } from "@/src/services/organization/organization.hooks";

export type CustomColor = Readonly<{
  id: string;
  label: string;
  hex: string;
}>;

const Appearance = () => {
  const {
    theme,
    primaryPalette,
    setTheme,
    setPrimaryPalette,
    savedThemeId,
    setSavedThemeId,
  } = useTheme();
  const { isSuperAdmin } = useAuth();

  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [customEnabled, setCustomEnabled] = useState(false);
  const { mutate: updateOrgColor } = useUpdateOrganizationColorMutation();
  const { data: orgColor } = useGetOrganizationColorQuery();

  const [customColors, setCustomColors] = useState<CustomColor[]>([
    { id: "primary-default", label: "Primary", hex: primaryPalette.default },
  ]);

  useEffect(() => {
    if (!orgColor?.primaryColor) return;
    
    const savedTheme = PRESET_THEMES.find((preset) => preset.color === orgColor.primaryColor);
    
    if (savedTheme) {
      setSelectedTheme(savedTheme.id);
      setCustomEnabled(false);
    } else {
      setCustomEnabled(true);
      setSelectedTheme(null);
      const updated = customColors.map((color) =>
        color.id === 'primary-default' ? { ...color, hex: orgColor.primaryColor } : color,
      );
      setCustomColors(updated);
      const primaryDefault =
        updated.find((color) => color.id === "primary-default")?.hex ??
        primaryPalette.default;
      setPrimaryPalette(derivePalette(primaryDefault));
    }
  }, [orgColor?.primaryColor]); // Add dependency

  const handleThemeSelect = (id: string) => {
    setSelectedTheme(id);
    setCustomEnabled(false);
  };

  const handleThemeApply = () => {
    if (!selectedTheme) return;
    const preset = PRESET_THEMES.find((preset) => preset.id === selectedTheme);
    if (!preset) return;

    setCustomEnabled(false);
    setPrimaryPalette(derivePalette(preset.color));
    setSavedThemeId(selectedTheme);
    updateOrgColor(preset.color as string);
  };

  const handleColorChange = (id: string, hex: string) => {
    setSelectedTheme(null);
    setSavedThemeId(null);
    const updated = customColors.map((color) =>
      color.id === id ? { ...color, hex } : color,
    );
    setCustomColors(updated);
    const primaryDefault =
      updated.find((color) => color.id === "primary-default")?.hex ??
      primaryPalette.default;
    setPrimaryPalette(derivePalette(primaryDefault));
    updateOrgColor(hex as string);
  };

  return (
    <View className="flex-1 gap-8">
      <ColorModeSection colorMode={theme as ColorMode} onSelect={setTheme} />
      {isSuperAdmin && (
        <CustomThemesSection
          enabled={customEnabled}
          onToggle={setCustomEnabled}
          colors={customColors}
          onColorChange={handleColorChange}
        />
      )}
      {isSuperAdmin && (
        <PresetThemesSection
          selectedTheme={selectedTheme}
          savedTheme={savedThemeId}
          onSelect={handleThemeSelect}
          onApply={handleThemeApply}
        />
      )}
      {isSuperAdmin && <OrgLogoSection />}
    </View>
  );
};

export default Appearance;
